/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Collaboration
 * (C) Copyright IBM Corp. 2014, 2021
 *
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

define('collaboration/api/sharing/GenerateImage',['underscore', 'jquery', '../../lib/@waca/core-client/js/core-client/ui/core/Class', '../../lib/@waca/core-client/js/core-client/utils/UniqueId', '../../lib/@waca/image-capture/dist/js/bundles/image-capture.min', '../../lib/@waca/core-client/js/core-client/utils/BrowserUtils'], function (_, $, Class, UniqueId, ImageCaptureLib, BrowserUtils) {

	var ImageCapture = ImageCaptureLib.default;

	var GenerateImage = Class.extend({

		/**
   * Image capture instance
   * @type {ImageCapture}
   */
		imageCapture: null,

		/**
   * @param options.elementMap - A map of css selectors --> SVG content.
   * Any children of the requested DOM node matching the selector will have their contents
   * replaced with the SVG. This is required for any cross-domain content that cannot be
   * rendered to the canvas.
   * @param options.logger (optional) If not null, this is expected to be a Glass logging object
   * @param {string[]} options.excludeClassList An optional array of elements identified by exclude classes to be removed from the cloned DOM.
   */
		init: function init(options) {
			GenerateImage.inherited('init', this, arguments);

			this.isIE11 = BrowserUtils.isIE11();
			options = options || {};
			this._logger = options.logger;
			this.imageCapture = new ImageCapture({
				UniqueId: UniqueId,
				proxy: options.proxy,
				elementMap: options.elementMap,
				excludeClassList: options.excludeClassList,
				glassLogger: options.logger
			});
		},

		_findPageSize: function _findPageSize(el) {
			var $el = $(el);
			return {
				'width': $el.width(),
				'height': $el.height()
			};
		},

		_getMaximumPageSize: function _getMaximumPageSize(pageElement) {
			var width = 0;
			var height = 0;

			$.each($(pageElement).find('.widget'), function (i, widget) {
				var $widget = $(widget);
				var offset = $widget.offset();

				width = Math.max(width, offset.left + $widget.outerWidth());
				height = Math.max(height, offset.top + $widget.outerHeight());
			});

			return {
				'width': width,
				'height': height
			};
		},

		_processBackgroundSVG: function _processBackgroundSVG(node) {
			if (node.style.backgroundImage) {
				node.style.backgroundSize = '100% 100%';
			}
		},

		onClone: function onClone(doc, node) {
			var _this = this;

			var $node = $(node);

			// fix visibility of dashboards/explore so that we can capture the whole thing
			var maxPage = this._findPageSize(node);
			$node.find('.widget:visible').each(function (i, widget) {
				var widgetStyles = window.getComputedStyle(widget);
				var isRelative = false;
				['height', 'width', 'top', 'left'].forEach(function (property) {
					if (widget.style[property].slice(-1) === '%') {
						widget.style[property] = widgetStyles.getPropertyValue(property);
						isRelative = true;
					}
				});
				if (_this.isIE11 && isRelative) {
					// This issue only affects IE11 with percentage values for height, width, top, left
					widget.style.setProperty('box-sizing', 'content-box', 'important');
				}
			});
			['.pagecontainer:visible', '.pageTabContent:visible', '.pagegenericPage:visible', '.explore-content__cards:visible', '.widgetContent:visible'].forEach(function (style) {
				$node.find(style).each(function (i, page) {
					var offscreenPageSize = this._getMaximumPageSize(page);
					maxPage.height = Math.max(maxPage.height, offscreenPageSize.height);
					maxPage.width = Math.max(maxPage.width, offscreenPageSize.width);
					page.style.setProperty('height', 'auto');
					page.style.setProperty('overflow', 'visible', 'important');
				}.bind(this));
			}.bind(this));
			var nodeOffset = $node.offset();
			node.style.setProperty('min-width', maxPage.width - nodeOffset.left + 'px', 'important');
			node.style.setProperty('min-height', maxPage.height - nodeOffset.top + 'px', 'important');
			node.style.setProperty('overflow', 'visible', 'important');

			// fix for dashboard's selected tab
			$node.find('.ba-common-tabList__item.is-selected').get().forEach(function (tab) {
				// Add in a rectangle that looks like the inset box-shadow used for indicating the active tab.
				// The backgroundColor comes from the style that is used for highlighting stuff.
				// The height and top are magic numbers that were picked based on how they made the line look.
				var $tab = $(tab);
				var style = window.getComputedStyle(tab);
				var boxShadow = style.getPropertyValue('box-shadow');
				var matchColors = /rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)/;
				var match = matchColors.exec(boxShadow);
				if (!match || !match.length) {
					// Stupid IE 11...
					matchColors = /(#[A-Fa-f0-9]{6})/;
					match = matchColors.exec(boxShadow);
					if (!match || !match.length) {
						if (this._logger) {
							this._logger.error('Unable to extract the color from ' + boxShadow);
						}
						return;
					}
				}
				$tab.css('border-bottom', '2px solid ' + match[0]);
				$tab.css('height', style.getPropertyValue('line-height'));
			}.bind(this));

			// fix for legend splitters that have overflowing items in the legend
			['.Legend.lgd-vertical', '.Legend.lgd-horizontal'].forEach(function (legends) {
				$node.find(legends).each(function (i, legend) {
					legend.style.setProperty('overflow', 'hidden');
				}.bind(this));
			}.bind(this));

			// fix to capture the full report.
			var $table = $node.find('table.pg, table.page');
			if ($table.length > 0) {
				node.style.height = 'auto';
				node.style.overflow = 'visible';
				$node.find('#idWorkarea_Pane, #idWorkareaPane, .clsHalScrollableBody').get().forEach(function (el) {
					el.style.height = 'auto';
					el.style.overflow = 'visible';
				});

				// fix for report tables overflowing
				$node.find('table.clsFixedHeightListBody > tbody').get().forEach(function (el) {
					el.style.overflow = 'auto';
				});

				// fix to correct letter spacing issues in reports
				$node.find('#idInsertableObjectsHeader_divTitle, #idSourcesPane_NoSources, #idSourcesPane_btnSelectSources, #idPropertiesPane_divEmptyMessage').get().forEach(function (el) {
					el.style.setProperty('letter-spacing', '0.05px');
				});
			}

			// fix for screenshots failing for reports in authoring mode in firefox
			if (BrowserUtils.isFirefox()) {
				$node.find('.clsVizPreview').get().forEach(this._processBackgroundSVG);
			}

			// fix for reports having incomplete absolute positioning
			$node.find('.clsHalScrollableBody').get().forEach(function (el) {
				el.style.bottom = '0';
				el.style.right = '0';
			});

			// fix for overview scene being cut off
			$node.find('.tabCntr').get().forEach(function (el) {
				el.style.overflow = 'visible';
			});

			if (this.isIE11) {
				// fix for text being squished in Explorations
				node.style.setProperty('letter-spacing', '0.05px');

				$node.find('.slotEditor__localFilters__heading__container, .slotEditor__footer__line, .slotEditor__header__line, .slotEditor__localFilters__heading__icon').get().forEach(function (el) {
					el.style.display = 'inline-block';
				});
			}

			// fix to hide zoom controls in viz
			$node.find('.ZoomParent').get().forEach(function (el) {
				el.style.display = 'none';
			});
		},

		destroy: function destroy() {
			if (this.imageCapture) {
				this.imageCapture.destroy();
			}
		},

		_getHeight: function _getHeight(domNode, resourceType) {
			var reportTypes = ['interactiveReport', 'report', 'reportView', 'dataSet2', 'query', 'analysis', 'output', 'reportVersion'];
			if (reportTypes.indexOf(resourceType) > -1) {
				var $domNode = $(domNode);
				var height = $domNode.height();
				$domNode.find('table.pg, table.page').get().forEach(function (node) {
					height = Math.max($(node).height(), height);
				});
				return height;
			}
			return null;
		},

		/**
   * Accept either an options object to support both the old and new invocations of generateImage().
   * @param {node} domNode - the domNode (not JQuery) that will be converted to a canvas
   * @param {object} options
   * @param {string} resourceType - specifies what kind of perpective is being passed in.
   */
		generateImage: function generateImage(domNode, options, resourceType) {
			var height = this._getHeight(domNode, resourceType);
			options = _.extend({
				onClone: this.onClone.bind(this),
				height: height,
				excludeEmptyIframes: true
			}, options);
			return this.imageCapture.generateImage(domNode, options);
		}
	});

	return GenerateImage;
});
;

var _temp = function () {
	if (typeof __REACT_HOT_LOADER__ === 'undefined') {
		return;
	}
}();

;

